import type { StandInfo, Status, UiVoll, VerlaufGruppe } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { schluessel, useStaende, useVerlauf } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { STATUS_ANZEIGE } from "../baum/StatusMarke.tsx";
import { Abschnitt } from "../eigenschaften/gemeinsam.tsx";
import { useDarf } from "../huelle/konto.ts";
import { personFarbe } from "../live/Anwesenheit.tsx";
import { Bestaetigung } from "../ui/Dialog.tsx";
import { useHinweis } from "../ui/Hinweise.tsx";
import "./planung.css";

const datum = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

const ARTEN: Record<string, string> = {
  slot_setzen: "Slot geändert",
  slots_leeren: "Slots geleert",
  slots_verschieben: "Slots verschoben",
  ui_setzen: "UI-Felder geändert",
  listenbereich_setzen: "Listenbereich gesetzt",
  listenbereich_entfernen: "Listenbereich entfernt",
  alles_ersetzen: "Inhalt ersetzt",
};

export function artText(art: string): string {
  const [kopf, rest] = art.split(":");
  if (kopf === "status" && rest) return `Status: ${STATUS_ANZEIGE[rest as Status]?.text ?? rest}`;
  if (kopf === "freigabe") return `Stand ${rest} freigegeben`;
  if (kopf === "im_spiel") return `Stand ${rest} im Spiel`;
  return ARTEN[art] ?? art;
}

function zusammenfassen(g: VerlaufGruppe): string {
  const zaehler = new Map<string, number>();
  for (const a of g.aenderungen) {
    const t = artText(a.art);
    zaehler.set(t, (zaehler.get(t) ?? 0) + 1);
  }
  return [...zaehler].map(([t, n]) => (n > 1 ? `${n}× ${t}` : t)).join(", ");
}

function StandZeile({ s, ui }: { s: StandInfo; ui: UiVoll }) {
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const darfBearbeiten = useDarf("bearbeiten");
  const darfFreigeben = useDarf("freigeben");
  const [fragen, setFragen] = useState(false);
  const neuLaden = () => Promise.all([schluessel.ui(ui.id), schluessel.staende(ui.id), schluessel.verlauf(ui.id), schluessel.struktur].map((k) => qc.invalidateQueries({ queryKey: k })));

  const ausfuehren = async (pfad: string, body: unknown, erfolg: string) => {
    try {
      await api(pfad, { methode: "POST", body });
      await neuLaden();
      hinweis({ text: erfolg });
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
    }
  };

  return (
    <li className="stand">
      <div className="stand-rumpf">
        <p className="stand-titel">
          Stand {s.nummer}
          {s.imSpiel && <span className="stand-marke">Im Spiel</span>}
          {s.nummer === ui.letzterStand && !ui.geaendertSeitStand && <span className="stand-marke stand-marke-aktuell">Aktuell</span>}
        </p>
        <p className="leise">
          {s.erstelltVon} · <time dateTime={s.erstelltAm}>{datum.format(new Date(s.erstelltAm))}</time>
        </p>
      </div>
      <div className="stand-aktionen">
        {darfFreigeben && !s.imSpiel && (
          <button type="button" className="knopf knopf-still" onClick={() => void ausfuehren(`/api/uis/${ui.id}/im-spiel`, { stand: s.nummer }, `Stand ${s.nummer} ist als „Im Spiel“ gemeldet.`)}>
            Im Spiel melden
          </button>
        )}
        {darfBearbeiten && (
          <button type="button" className="knopf knopf-still" onClick={() => setFragen(true)}>
            Zurückholen
          </button>
        )}
      </div>
      <Bestaetigung
        offen={fragen}
        titel={`Stand ${s.nummer} zurückholen?`}
        bestaetigen="Zurückholen"
        onAbbrechen={() => setFragen(false)}
        onBestaetigen={() => {
          setFragen(false);
          void ausfuehren(`/api/uis/${ui.id}/staende/${s.nummer}/zurueck`, undefined, `Inhalt von Stand ${s.nummer} ist zurückgeholt.`);
        }}
      >
        <p>Die UI bekommt wieder den Inhalt von Stand {s.nummer}. Das ist eine neue Änderung; der heutige Inhalt bleibt im Verlauf.</p>
      </Bestaetigung>
    </li>
  );
}

export function Verlauf({ ui, onSchliessen }: { ui: UiVoll; onSchliessen: () => void }) {
  const staende = useStaende(ui.id);
  const verlauf = useVerlauf(ui.id);
  return (
    <aside className="eigenschaften" aria-label="Verlauf">
      <header className="panel-kopf panel-kopf-zeile">
        <h2>Verlauf</h2>
        <button type="button" className="knopf knopf-still knopf-symbol" aria-label="Verlauf schließen" title="Verlauf schließen" onClick={onSchliessen}>
          <X size={15} aria-hidden="true" />
        </button>
      </header>
      <Abschnitt region titel="Stände">
        {staende.data?.length === 0 && <p className="leise">Noch nicht freigegeben.</p>}
        {!!staende.data?.length && (
          <ul className="staende">
            {[...staende.data].reverse().map((s) => (
              <StandZeile key={s.nummer} s={s} ui={ui} />
            ))}
          </ul>
        )}
      </Abschnitt>
      <Abschnitt region titel="Änderungen">
        {verlauf.data?.length === 0 && <p className="leise">Noch keine Änderungen.</p>}
        {!!verlauf.data?.length && (
          <ol className="verlauf">
            {verlauf.data.map((g, i) => (
              <li key={i} className="verlauf-gruppe">
                <span className="notiz-person" style={{ background: personFarbe(g.autor, g.autor === "Claude") }} aria-hidden="true">
                  {g.autor.slice(0, 1).toUpperCase()}
                </span>
                <div>
                  <p className="verlauf-kopf">
                    <strong>{g.autor}</strong> <time dateTime={g.minute}>{datum.format(new Date(g.aenderungen[0]!.zeit))}</time>
                  </p>
                  <p className="verlauf-text">{zusammenfassen(g)}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Abschnitt>
    </aside>
  );
}
